/**
 * AuthContext — OmniCheck Studio Authentication System
 * Supports both httpOnly cookie auth (secure) and legacy localStorage token (transitional)
 */
import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { apiUrl } from '../config';
import { isAuthFailureStatus, AUTH_EXPIRED_EVENT } from '../utils/authExpiry';

export interface User {
  id: number | string;
  email: string;
  role: 'user' | 'admin';
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isLoading: boolean;
  authModalOpen: boolean;
  authModalMode: 'login' | 'register';
  openAuthModal: (mode?: 'login' | 'register') => void;
  closeAuthModal: () => void;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  register: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const TOKEN_KEY = 'token';
const USER_KEY = 'user_data';

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [token, setToken] = useState<string | null>(() => localStorage.getItem(TOKEN_KEY));
  const [user, setUser] = useState<User | null>(() => {
    const stored = localStorage.getItem(USER_KEY);
    if (stored) { try { return JSON.parse(stored); } catch (e) { return null; } }
    return null;
  });
  const [isLoading, setIsLoading] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authModalMode, setAuthModalMode] = useState<'login' | 'register'>('login');
  const [authInitialized, setAuthInitialized] = useState(false);
  // Set when /api/auth/me verified a cookie-only session (no stored
  // Bearer token): the session is real even though `token` is null.
  const [cookieVerified, setCookieVerified] = useState(false);

  // Drop every trace of the local session (state + storage). Used by
  // logout and by auth reconciliation: when the server says the
  // credentials are dead, the app must stop presenting the user as
  // signed in (2026-11-04: an expired token left the avatar up while
  // every account save failed behind it).
  const clearStoredSession = useCallback(() => {
    setToken(null);
    setUser(null);
    setCookieVerified(false);
    try {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
    } catch (e) { /* storage unavailable */ }
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem(TOKEN_KEY);
      const storedUser = localStorage.getItem(USER_KEY);
      // Probe with EVERY credential we hold: the cookie AND the
      // stored Bearer token. The old probe sent no Authorization
      // header, so a perfectly good localStorage session 401'd
      // whenever the cookie was missing - and the fallback then
      // trusted the stored user without any validation at all,
      // which is how a dead token kept a signed-in face.
      let verdict: 'ok' | 'rejected' | 'unknown' = 'unknown';
      let serverUser: User | null = null;
      try {
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (storedToken) headers['Authorization'] = `Bearer ${storedToken}`;
        const response = await fetch(apiUrl('/api/auth/me'), {
          method: 'GET',
          credentials: 'include',
          headers,
        });
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.user) { verdict = 'ok'; serverUser = data.user; }
        } else if (isAuthFailureStatus(response.status)) {
          verdict = 'rejected';
        }
      } catch (err) { /* network down / server unreachable: unknown, not rejected */ }
      if (verdict === 'ok' && serverUser) {
        setUser(serverUser);
        try { localStorage.setItem(USER_KEY, JSON.stringify(serverUser)); } catch (e) { /* ignore */ }
        if (storedToken) setToken(storedToken);
        else setCookieVerified(true);
        setAuthInitialized(true);
        return;
      }
      if (verdict === 'rejected') {
        // The server explicitly rejected these credentials (expired
        // or invalid). Reconcile to a truthful signed-out state.
        clearStoredSession();
        setAuthInitialized(true);
        return;
      }
      // Unknown (offline, server error): keep the stored session as
      // a local identity so a transient outage doesn't sign anyone
      // out. Account calls reconcile on their own 401/403 and raise
      // AUTH_EXPIRED_EVENT if the credentials turn out to be dead.
      if (storedToken && storedUser) {
        try {
          const parsedUser = JSON.parse(storedUser);
          setToken(storedToken);
          setUser(parsedUser);
        } catch (e) { clearStoredSession(); }
      }
      setAuthInitialized(true);
    };
    initAuth();
  }, [clearStoredSession]);

  // Any part of the app that learns the session is dead (a designs
  // call answering 401/403, say) raises AUTH_EXPIRED_EVENT; the
  // whole app reconciles here, once.
  useEffect(() => {
    const onExpired = () => { clearStoredSession(); };
    window.addEventListener(AUTH_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, onExpired);
  }, [clearStoredSession]);

  const openAuthModal = useCallback((mode: 'login' | 'register' = 'login') => {
    setAuthModalMode(mode); setAuthModalOpen(true);
  }, []);

  const closeAuthModal = useCallback(() => { setAuthModalOpen(false); }, []);

  const handleAuthSuccess = (newToken: string, newUser: User) => {
    setToken(newToken); setUser(newUser);
    localStorage.setItem(TOKEN_KEY, newToken);
    localStorage.setItem(USER_KEY, JSON.stringify(newUser));
    closeAuthModal();
  };

  const logout = useCallback(async () => {
    try { await fetch(apiUrl('/api/auth/logout'), { method: 'POST', credentials: 'include' }); } catch (err) {}
    clearStoredSession();
  }, [clearStoredSession]);

  const login = async (email: string, password: string): Promise<{ success: boolean; error?: string }> => {
    setIsLoading(true);
    try {
      const response = await fetch(apiUrl('/api/auth/login'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
        credentials: 'include',
      });
      const data = await response.json();
      if (!response.ok || !data.success) return { success: false, error: data.error || 'Login failed. Please check your credentials.' };
      if (data.token) handleAuthSuccess(data.token, data.user);
      else { setUser(data.user); closeAuthModal(); }
      return { success: true };
    } catch (err: any) {
      return { success: false, error: err.message || 'Network error connecting to auth server.' };
    } finally { setIsLoading(false); }
  };

  const register = async (email: string, password: string): Promise<{ success: boolean; error?: string }> => {
    setIsLoading(true);
    try {
      const response = await fetch(apiUrl('/api/auth/register'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
        credentials: 'include',
      });
      const data = await response.json();
      if (!response.ok || !data.success) return { success: false, error: data.error || 'Registration failed.' };
      if (data.token) handleAuthSuccess(data.token, data.user);
      else { setUser(data.user); closeAuthModal(); }
      return { success: true };
    } catch (err: any) {
      return { success: false, error: err.message || 'Network error connecting to auth server.' };
    } finally { setIsLoading(false); }
  };

  const refreshUser = useCallback(async () => {
    try {
      const storedToken = localStorage.getItem(TOKEN_KEY);
      const headers: Record<string, string> = {};
      if (storedToken) headers['Authorization'] = `Bearer ${storedToken}`;
      const response = await fetch(apiUrl('/api/auth/me'), { method: 'GET', credentials: 'include', headers });
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.user) { setUser(data.user); localStorage.setItem(USER_KEY, JSON.stringify(data.user)); }
      } else if (isAuthFailureStatus(response.status)) {
        // The session died since it was stored: stop presenting a
        // signed-in user on dead credentials.
        clearStoredSession();
      }
    } catch (err) { console.error('Failed to refresh user:', err); }
  }, [clearStoredSession]);

  const isAuthenticated = Boolean(((token || cookieVerified) && user));
  const isAdmin = user?.role === 'admin';

  return (
    <AuthContext.Provider value={{ user, token, isAuthenticated, isAdmin, isLoading, authModalOpen, authModalMode, openAuthModal, closeAuthModal, login, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};
